import { useState, useMemo, useCallback, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTreeDetail, useTreeArborInspections, useArborInspectionDetail, useTreeTimeline, useTreeEvidence } from "@/hooks";
import { buildFCADataFromApi } from "@/lib/fca-mapper";
import { estadoFitoConfig, riscoConfig } from "@/config/cta-config";
import { occurrenceApi, type OccurrenceSummaryApi, type TreeApi } from "@/lib/api";
import { hasPermission } from "@/lib/auth";
import { exportTreeCsv } from "@/lib/treeExportCsv";
import { fichaUrl } from "@/components/shared/QrCode";
import { OCCURRENCE_CLOSED_STATUSES } from "@/config/occurrence-config";
import type { FCAData } from "@/types/fca";
import TreeProfileHeader from "./components/TreeProfileHeader";
import TreeCurrentSituation from "./components/TreeCurrentSituation";
import TreeQuickStats from "./components/TreeQuickStats";
import TreeTabs from "./components/TreeTabs";
import TreeLocationCard from "./components/TreeLocationCard";
import TreeBotanicalCard from "./components/TreeBotanicalCard";
import TreeHealthRiskPanel from "./components/TreeHealthRiskPanel";
import TreeInspectionTable from "./components/TreeInspectionTable";
import TreePhotoGallery from "./components/TreePhotoGallery";
import TreeInterferenceList from "./components/TreeInterferenceList";
import TreeManagementPanel from "./components/TreeManagementPanel";
import TreeDocumentsTable from "./components/TreeDocumentsTable";
import TreeTimeline from "./components/TreeTimeline";
import EditTreeModal from "./components/EditTreeModal";
import RegisterOccurrenceModal from "./components/RegisterOccurrenceModal";
import TreeOccurrences from "./components/TreeOccurrences";
import type { MoreAction } from "./components/TreeMoreActions";

export default function FCAPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("visao-geral");
  const [editing, setEditing] = useState(false);
  const [registeringOccurrence, setRegisteringOccurrence] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const numericId = id ? parseInt(id) : null;
  const { data: treeRaw, loading: treeLoading, error: treeError } = useTreeDetail(numericId);
  const { inspections, loading: inspLoading, refetch: refetchInspections } = useTreeArborInspections(numericId);

  // A condicao ATUAL e a da vistoria validada mais recente: uma nova vistoria nao altera o
  // passado, e rascunho ou revisao em andamento nao e estado do exemplar.  Antes, sem nenhuma
  // validada, a ficha exibia a ultima vistoria mesmo em rascunho.
  const latestValidatedId = useMemo(() => {
    const validated = inspections.filter((i) => i.workflowStatus === "VALIDATED" || i.workflowStatus === "CONCLUDED");
    return validated.length > 0 ? validated[0].id : null;
  }, [inspections]);

  const { events: timelineEvents } = useTreeTimeline(numericId);
  const { evidence: treeEvidence, refetch: refetchEvidence } = useTreeEvidence(numericId);

  const { inspection: latestDetail } = useArborInspectionDetail(latestValidatedId);

  // Ocorrencias do exemplar (OC-1).  Sem occurrence.read a aba nem e montada.
  const canReadOccurrences = hasPermission("occurrence.read");
  const canCreateOccurrence = hasPermission("occurrence.create");
  const [occurrences, setOccurrences] = useState<OccurrenceSummaryApi[]>([]);
  const [occLoading, setOccLoading] = useState(false);
  const [occError, setOccError] = useState<string | null>(null);
  const loadOccurrences = useCallback(() => {
    if (!numericId || !canReadOccurrences) return;
    setOccLoading(true);
    setOccError(null);
    occurrenceApi
      .listForTree(numericId)
      .then(setOccurrences)
      .catch((e) => setOccError(e instanceof Error ? e.message : "Não foi possível carregar as ocorrências."))
      .finally(() => setOccLoading(false));
  }, [numericId, canReadOccurrences]);
  useEffect(loadOccurrences, [loadOccurrences]);

  useEffect(() => {
    if (!notice) return;
    const t = window.setTimeout(() => setNotice(null), 3500);
    return () => window.clearTimeout(t);
  }, [notice]);

  const data: FCAData | null = useMemo(() => {
    if (!treeRaw) return null;
    return buildFCADataFromApi(treeRaw as TreeApi, inspections, latestDetail, timelineEvents, treeEvidence);
  }, [treeRaw, inspections, latestDetail, timelineEvents, treeEvidence]);

  if ((treeLoading || inspLoading) && !treeRaw) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
          <p className="text-sm text-foreground-500 mt-3">Carregando ficha cadastral...</p>
        </div>
      </div>
    );
  }

  if (treeError || !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center max-w-md">
          <i className="ri-tree-line text-4xl text-foreground-300"></i>
          <h2 className="text-lg font-semibold text-foreground-800 mt-3">Árvore não encontrada</h2>
          <p className="text-sm text-foreground-500 mt-1">
            {treeError ?? `Não foi possível localizar a árvore com ID ${id}.`}
          </p>
          <button
            onClick={() => navigate("/cta")}
            className="btn-primary text-sm mt-4"
          >
            <i className="ri-arrow-left-line mr-1"></i>
            Voltar ao CTA
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (dateStr: string) => {
    if (!dateStr || dateStr === "—") return "—";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return dateStr;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const ef = estadoFitoConfig[data.estadoFitossanitario] || { badge: "", label: data.estadoFitossanitario || "—" };
  const rk = riscoConfig[data.classeRisco] || { badge: "", label: data.classeRisco || "—" };

  const renderTab = () => {
    switch (activeTab) {
      case "visao-geral":
        return <TabVisaoGeral data={data} ef={ef} rk={rk} formatDate={formatDate} />;
      case "localizacao":
        return <TabContentWrapper><TreeLocationCard data={data} /></TabContentWrapper>;
      case "botanicos":
        return <TabContentWrapper><TreeBotanicalCard data={data} /></TabContentWrapper>;
      case "fitossanidade":
        return <TabContentWrapper><TreeHealthRiskPanel data={data} /></TabContentWrapper>;
      case "vistorias":
        return <TabContentWrapper><TreeInspectionTable vistorias={data.vistorias} /></TabContentWrapper>;
      case "fotos":
        return <TabContentWrapper><TreePhotoGallery treeId={numericId} evidence={treeEvidence} onPhotosAdded={() => { refetchEvidence(); refetchInspections(); }} /></TabContentWrapper>;
      case "interferencias":
        return <TabContentWrapper><TreeInterferenceList interferencias={data.interferencias} /></TabContentWrapper>;
      case "ocorrencias":
        return (
          <TabContentWrapper>
            <TreeOccurrences
              occurrences={occurrences}
              loading={occLoading}
              error={canReadOccurrences ? occError : "Seu perfil não tem acesso às ocorrências."}
              onRegister={canCreateOccurrence ? () => setRegisteringOccurrence(true) : undefined}
            />
          </TabContentWrapper>
        );
      case "manejo":
        return <TabContentWrapper><TreeManagementPanel manejo={data.manejo} /></TabContentWrapper>;
      case "documentos":
        return <TabContentWrapper><TreeDocumentsTable documentos={data.documentos} /></TabContentWrapper>;
      case "historico":
        return <TabContentWrapper><TreeTimeline timeline={data.timeline} /></TabContentWrapper>;
      default:
        return null;
    }
  };

  const openTab = (tab: string, anchor = "fca-tabs") => {
    setActiveTab(tab);
    // Duas passadas: a primeira monta a aba, a segunda acha a ancora dentro dela.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => document.getElementById(anchor)?.scrollIntoView({ behavior: "smooth", block: "start" })),
    );
  };

  const copyLink = async () => {
    const url = fichaUrl(data.id);
    try {
      await navigator.clipboard.writeText(url);
      setNotice("Link da ficha copiado.");
    } catch {
      window.prompt("Copie o link da ficha:", url);
    }
  };

  const openOccurrences = occurrences.filter((o) => !OCCURRENCE_CLOSED_STATUSES.has(o.status)).length;
  const moreActions: MoreAction[] = [
    ...(canCreateOccurrence
      ? [{ key: "oc-new", label: "Registrar ocorrência", icon: "ri-alarm-warning-line", hint: "Queda, galho caído, praga, dano, supressão irregular.", onClick: () => setRegisteringOccurrence(true) }]
      : []),
    ...(canReadOccurrences
      ? [{
          key: "oc-list",
          label: occurrences.length > 0 ? `Ver ocorrências (${occurrences.length}${openOccurrences ? `, ${openOccurrences} em aberto` : ""})` : "Ver ocorrências",
          icon: "ri-list-check-2",
          onClick: () => openTab("ocorrencias"),
        }]
      : []),
    { key: "lot", label: "Confirmar relação com o lote", icon: "ri-community-line", hint: "Calçada, dentro do lote ou divisa.", onClick: () => openTab("localizacao", "fca-lot-relation") },
    { key: "history", label: "Histórico completo", icon: "ri-history-line", onClick: () => openTab("historico") },
    { key: "link", label: "Copiar link da ficha", icon: "ri-link", separatorBefore: true, onClick: copyLink },
    { key: "label", label: "Imprimir etiqueta com QR Code", icon: "ri-qr-code-line", hint: "90 × 55 mm, para fixar no exemplar.", onClick: () => navigate(`/cta/${data.id}/etiqueta`) },
    { key: "csv", label: "Exportar dados do exemplar (CSV)", icon: "ri-file-excel-2-line", hint: "Cadastro e vistorias.", onClick: () => exportTreeCsv(data) },
  ];

  const tree = treeRaw as TreeApi | null;

  return (
    <div className="space-y-4">
      {notice && (
        <div role="status" className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-foreground-900 text-white text-sm px-4 py-2 rounded-lg shadow-lg">
          {notice}
        </div>
      )}
      {registeringOccurrence && tree && (
        <RegisterOccurrenceModal
          treeId={tree.id}
          codigoCta={data.codigoCta}
          latitude={tree.latitude}
          longitude={tree.longitude}
          projectId={tree.project_id}
          street={tree.street}
          neighborhood={tree.neighborhood}
          onClose={() => setRegisteringOccurrence(false)}
          onCreated={loadOccurrences}
        />
      )}
      <button type="button" onClick={() => navigate("/cta")} className="btn-outline text-sm">
        <i className="ri-arrow-left-line" aria-hidden="true"></i>
        Voltar ao CTA
      </button>
      {editing && treeRaw && (
        <EditTreeModal
          tree={treeRaw as TreeApi}
          onClose={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            // Recarrega a ficha para refletir a correcao e o novo evento no historico.
            navigate(0);
          }}
        />
      )}
      <TreeProfileHeader
        data={data}
        onEdit={() => setEditing(true)}
        onAddPhotos={() => openTab("fotos")}
        moreActions={moreActions}
      />
      {treeRaw && (
        <TreeCurrentSituation
          tree={treeRaw as TreeApi}
          inspections={inspections}
          validated={latestValidatedId ? latestDetail : null}
        />
      )}
      <TreeQuickStats data={data} />
      <div id="fca-tabs" className="scroll-mt-4">
        <TreeTabs activeTab={activeTab} onChange={setActiveTab} />
      </div>
      <div className="min-h-[400px]">
        {renderTab()}
      </div>
    </div>
  );
}

function TabContentWrapper({ children }: { children: React.ReactNode }) {
  return <div className="mt-0">{children}</div>;
}

function TabVisaoGeral({
  data,
  ef,
  rk,
  formatDate,
}: {
  data: FCAData;
  ef: { badge: string; label: string };
  rk: { badge: string; label: string };
  formatDate: (d: string) => string;
}) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <div className="bg-white rounded-card border border-background-200/70 p-5">
            <h3 className="text-sm font-semibold text-foreground-900 font-heading mb-3 flex items-center gap-2">
              <i className="ri-eye-line text-accent-500"></i>
              Resumo Executivo
            </h3>
            <p className="text-sm text-foreground-700 leading-relaxed">
              Exemplar arbóreo da espécie <strong>{data.nomePopular}</strong>{data.nomeCientifico && data.nomeCientifico !== "—" && <> (<em>{data.nomeCientifico}</em>)</>}, cadastrado no CTA de {data.municipio}/{data.uf} em {formatDate(data.dataCadastro)}
              {ef.label !== "—" && (
                <> Encontra-se em <span className={`badge text-xs mx-0.5 align-middle ${ef.badge}`}>{ef.label}</span> estado fitossanitário</>
              )}
              {rk.label !== "—" && (
                <> e classificado como risco <span className={`badge text-xs mx-0.5 align-middle ${rk.badge}`}>{rk.label}</span></>
              )}
              .
              {data.dap > 0 && <> Possui DAP de {data.dap} cm, altura de {data.altura} m e diâmetro de copa de {data.diametroCopa} m.</>}
              {data.ultimaVistoria && data.ultimaVistoria !== "—" && (
                <> A última vistoria foi realizada em {formatDate(data.ultimaVistoria)} pelo técnico {data.tecnicoResponsavel}.</>
              )}
              {data.manejoRecomendado && data.manejoRecomendado !== "—" && (
                <> Recomendação: <strong>{data.manejoRecomendado}</strong>.</>
              )}
              {(data.fotosCount > 0 || data.vistorias.length > 0) && (
                <> Conta com {data.fotosCount} fotos, {data.vistorias.length} vistorias e {data.documentos.length} documentos vinculados à sua ficha cadastral.</>
              )}
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <MiniCard icon="ri-plant-line" label="Tipo" value={data.tipo} color="primary" />
            <MiniCard icon="ri-calendar-line" label="Cadastro" value={formatDate(data.dataCadastro)} color="accent" />
            <MiniCard icon="ri-user-line" label="Responsável" value={data.tecnicoResponsavel} color="secondary" />
            <MiniCard icon="ri-folder-chart-line" label="Projeto" value={data.projetoVinculado.split("—")[0].trim()} color="accent" />
          </div>

          {data.manejoRecomendado && data.manejoRecomendado !== "—" && (
            <div className="bg-white rounded-card border border-background-200/70 p-5">
              <h3 className="text-sm font-semibold text-foreground-900 font-heading mb-3 flex items-center gap-2">
                <i className="ri-tools-line text-primary-500"></i>
                Última Recomendação Técnica
              </h3>
              <div className="flex items-start gap-3 p-3 bg-primary-50/30 rounded-lg border border-primary-200/50">
                <span className="w-9 h-9 rounded-md bg-primary-100 flex items-center justify-center shrink-0">
                  <i className="ri-lightbulb-line text-primary-600"></i>
                </span>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">{data.manejoRecomendado}</p>
                  {data.fitossanidade.parecerTecnico && data.fitossanidade.parecerTecnico !== "—" && (
                    <p className="text-xs text-foreground-600 mt-0.5">{data.fitossanidade.parecerTecnico.substring(0, 200)}...</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-card border border-background-200/70 p-4">
            <h3 className="text-sm font-semibold text-foreground-900 font-heading mb-3 flex items-center gap-2">
              <i className="ri-notification-3-line text-warning-500"></i>
              Alertas
            </h3>
            {data.alertas.length === 0 ? (
              <p className="text-xs text-foreground-500">Nenhum alerta ativo</p>
            ) : (
              <div className="space-y-2">
                {data.alertas.map((a, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 bg-accent-50/30 rounded-lg border border-accent-200/50">
                    <i className={`text-sm mt-0.5 ${
                      a.nivel === "info" ? "ri-information-line text-accent-600" :
                      a.nivel === "warning" ? "ri-alert-line text-warning-600" :
                      "ri-error-warning-line text-danger-600"
                    }`}></i>
                    <p className="text-xs text-foreground-700 leading-relaxed">{a.mensagem}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {data.timeline.length > 0 && (
            <div className="bg-white rounded-card border border-background-200/70 p-4">
              <h3 className="text-sm font-semibold text-foreground-900 font-heading mb-3 flex items-center gap-2">
                <i className="ri-history-line text-foreground-600"></i>
                Últimos Eventos
              </h3>
              <div className="space-y-3">
                {data.timeline.slice(0, 4).map((evt) => (
                  <div key={evt.id} className="flex gap-2.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent-400 mt-1.5 shrink-0"></span>
                    <div className="min-w-0">
                      <p className="text-xs text-foreground-900 font-medium leading-tight">{evt.titulo}</p>
                      <p className="text-[10px] text-foreground-500">{formatDate(evt.data)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {data.vistorias.length > 0 && (
            <div className="bg-white rounded-card border border-background-200/70 p-4">
              <h3 className="text-sm font-semibold text-foreground-900 font-heading mb-3 flex items-center gap-2">
                <i className="ri-clipboard-line text-primary-500"></i>
                Vistorias ({data.vistorias.length})
              </h3>
              <div className="space-y-2">
                {data.vistorias.slice(0, 3).map((v) => (
                  <div key={v.id} className="flex items-center justify-between p-2 bg-background-50 rounded-lg">
                    <div>
                      <p className="text-xs font-medium text-foreground-800">{v.tipo}</p>
                      <p className="text-[10px] text-foreground-500">{formatDate(v.data)}</p>
                    </div>
                    <span className={`badge text-[10px] ${
                      v.status === "Concluída" || v.status === "Validada"
                        ? "bg-primary-100 text-primary-700"
                        : "bg-warning-100 text-warning-700"
                    }`}>
                      {v.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniCard({ icon, label, value, color }: { icon: string; label: string; value: string; color: string }) {
  const colors: Record<string, { bg: string; icon: string; bar: string }> = {
    primary: { bg: "bg-primary-50", icon: "text-primary-600", bar: "bg-primary-500" },
    accent: { bg: "bg-accent-50", icon: "text-accent-600", bar: "bg-accent-500" },
    secondary: { bg: "bg-secondary-50", icon: "text-secondary-600", bar: "bg-secondary-500" },
    warning: { bg: "bg-warning-50", icon: "text-warning-600", bar: "bg-warning-500" },
  };
  const c = colors[color] || colors.primary;

  return (
    <div className="bg-white rounded-card border border-background-200/70 p-3 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-[3px] ${c.bar}`}></div>
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`w-6 h-6 rounded-md ${c.bg} flex items-center justify-center`}>
          <i className={`${icon} ${c.icon} text-xs`}></i>
        </span>
      </div>
      <p className="text-xs text-foreground-900 font-medium leading-tight truncate">{value}</p>
      <p className="text-[10px] text-foreground-500">{label}</p>
    </div>
  );
}
